// @src/pre-deployment/src/components/learning/GrammarLearn.js v1.0
// Interactive Grammar Module for Level 2 Chapter 1
// Click-to-build sentence construction with word conjugation

import React, { useState, useEffect, useMemo } from 'react';

import { useAuth } from '../../contexts/AuthContext';
import { GrammarTracking, ChapterProgress } from '../../data/tracking';
import { useGrammarPoints, useSessionProgressActions } from '../../data/useDataService';
import { TrackingV2Service } from '../../tracking/trackingService.js';
import './GrammarLearn.css';

// COMPREHENSIVE HARDCODED BACKUP DATA - Based on actual Firestore structure
// Moved outside component to prevent recreation on every render
const hardcodedGrammarData = {
    learning: [
        {
            id: 'ch1_001',
            title_kr: '-고 있다',
            title_en: 'Is doing (progressive form)',
            grammarPoint: '-고 있어요 (Progressive tense)',
            explanation: 'Used to express ongoing actions. Attach -고 있다 directly to verb stems to indicate an action is currently happening.',
            examples: [
                '지금 음악을 듣고 있어요. (I am listening to music now.)',
                '동생은 텔레비전을 보고 있어요. (My younger sibling is watching television.)',
                '아버지께서 지금 회사에서 일하고 계세요. (My father is working at the company now.)'
            ],
            // Word bank structure for the game - based on actual Korean grammar
            wordBank: [
                { original: "지금", meaning: "now", position: 1, conjugated: "지금" },
                { original: "음악을", meaning: "music", position: 2, conjugated: "음악을" },
                { original: "듣고", meaning: "listening", position: 3, conjugated: "듣고" },
                { original: "있어요", meaning: "am", position: 4, conjugated: "있어요" }
            ],
            english: "I am listening to music now",
            korean: "지금 음악을 듣고 있어요",
            grammarPoints: [
                "Attach -고 있다 directly to verb stems (remove 다)",
                "Used only with action verbs, not descriptive verbs",
                "Tense is indicated by changing 있다 to past (있었다) or future (있을 것이다)"
            ],
            forms: {
                "basic_present": "-고 있다",
                "polite_present": "-고 있어요",
                "informal_present": "-고 있어",
                "past_progressive": "-고 있었다",
                "honorific": "-고 계시다"
            },
            register: "polite",
            grammar_group: "Verb Endings & Modals"
        },
        {
            id: 'ch1_002',
            title_kr: '-고 있다',
            title_en: 'Is doing (progressive form)',
            grammarPoint: '-고 있어요 (Progressive tense)',
            explanation: 'Used to express ongoing actions in different contexts.',
            examples: [
                '비가 오고 있어요. (It is raining.)',
                '저는 그 사실을 알고 있어요. (I know that fact.)'
            ],
            wordBank: [
                { original: "비가", meaning: "rain", position: 1, conjugated: "비가" },
                { original: "오고", meaning: "falling", position: 2, conjugated: "오고" },
                { original: "있어요", meaning: "is", position: 3, conjugated: "있어요" }
            ],
            english: "It is raining",
            korean: "비가 오고 있어요",
            grammarPoints: [
                "Used with action verbs to show ongoing activity",
                "Can be used with weather verbs like 오다 (to come/fall)"
            ],
            register: "polite",
            grammar_group: "Verb Endings & Modals"
        }
    ],
    exercises: [],
    total: 2
};

const GrammarFeature = ({ _moduleKey, _moduleConfig, _navigation, _modulesOrder, _moduleMap, level, chapter }) => {
    const { user } = useAuth();

    const sessionType = 'Learn';
    const moduleType = 'grammar';

    // useFirestore toggle REMOVED — always uses Firestore when data is available (see useMemo below)

    // Get grammar data from data service (same as GrammarFeature.js)
    const {
        data: fetchedGrammarData,
        loading: grammarLoading,
        error: grammarError
    } = useGrammarPoints(level, chapter, 'Korea_University');

    // Data source selection with comprehensive logging
    const { grammarData, dataSource } = useMemo(() => {
        // Prioritize Firestore if data is available and valid
        const hasFirestoreData = fetchedGrammarData &&
            (fetchedGrammarData.learning?.length > 0 || fetchedGrammarData.total > 0);

        if (hasFirestoreData) {
            // Checking useFirestore is redundant if we want to default to available data, 
            // but keeping the toggle functionality requires respect for user choice if implemented strictly.
            // However, typical behavior is "Use best available".

            // Let's use Firestore if explicitly enabled OR if it's the only valid source and hardcoded is empty (unlikely).
            // Better logic: Default to Firestore if available, unless user forces backup (but UI implies toggle enables Firestore).
            // Given the bug "defaults to false", we should allow auto-selection.

            console.group('🔥 FIRESTORE DATA LOADED');
            console.log('📊 Firestore data structure:', {
                hasLearning: !!fetchedGrammarData.learning,
                learningLength: fetchedGrammarData.learning?.length,
                firstItem: fetchedGrammarData.learning?.[0],
                keys: Object.keys(fetchedGrammarData)
            });
            console.log('📊 Raw Firestore data:', fetchedGrammarData);
            console.groupEnd();
            return { grammarData: fetchedGrammarData, dataSource: 'Firestore' };
        } else {
            console.group('💾 HARDCODED BACKUP DATA LOADED');
            console.log('📊 Hardcoded data structure:', {
                hasLearning: !!hardcodedGrammarData.learning,
                learningLength: hardcodedGrammarData.learning?.length,
                firstItem: hardcodedGrammarData.learning?.[0]
            });
            console.log('📊 Hardcoded data:', hardcodedGrammarData);
            console.groupEnd();
            return { grammarData: hardcodedGrammarData, dataSource: 'Hardcoded Backup' };
        }
    }, [fetchedGrammarData]);

    // Convert learning data to exercise format for the game - handles both Firestore and hardcoded data
    const grammarExercises = useMemo(() => {
        // Handle if grammarData is the array itself or has .learning or .grammars property
        const rawData = Array.isArray(grammarData) ? grammarData :
            (grammarData?.learning || grammarData?.grammars || []);

        if (!rawData || rawData.length === 0) return [];

        return rawData.map((grammar, index) => {
            console.group(`🔄 CONVERTING GRAMMAR DATA - Item ${index}`);
            console.log('📊 Raw grammar item:', grammar);
            console.log('📊 Data source:', dataSource);

            // Handle different data structures from Firestore vs hardcoded
            let wordBank = grammar.wordBank;
            let english = grammar.english;
            let korean = grammar.korean;
            let grammarPoints = grammar.grammarPoints;

            // If no wordBank exists, create one from examples or use fallback
            if (!wordBank || wordBank.length === 0) {
                console.log('⚠️ No wordBank found, creating from examples or using fallback');
                // Check multiple example sources
                const rawExamples = grammar.examples || grammar.step1?.examples || grammar.regular_examples || [];

                if (rawExamples && rawExamples.length > 0) {
                    // Extract from Firestore examples
                    // Handle both object examples and string examples
                    const example = rawExamples[0];
                    let koreanText = '';
                    let englishText = '';

                    if (typeof example === 'string') {
                        koreanText = example;
                        englishText = 'Translate this sentence';
                    } else {
                        koreanText = example.sentence_kr || example.text || example.korean || '';
                        englishText = example.translation || example.english || '';
                    }

                    // Simple word extraction (this could be more sophisticated)
                    if (koreanText) {
                        const words = koreanText.split(' ').filter(word => word.trim());
                        wordBank = words.map((word, pos) => ({
                            original: word,
                            meaning: `part ${pos + 1}`,
                            position: pos + 1,
                            conjugated: word
                        }));
                        english = englishText;
                        korean = koreanText;
                    }
                }

                if (!wordBank || wordBank.length === 0) {
                    // Fallback wordBank
                    wordBank = [
                        { original: "예시", meaning: "example", position: 1, conjugated: "예시" },
                        { original: "문장", meaning: "sentence", position: 2, conjugated: "문장" }
                    ];
                    english = "Build the sentence";
                    korean = "예시 문장";
                }
            }

            // Handle grammar points
            if (!grammarPoints || grammarPoints.length === 0) {
                if (grammar.explanation) {
                    grammarPoints = [grammar.explanation];
                } else if (grammar.rules?.usage_rules) {
                    grammarPoints = grammar.rules.usage_rules;
                } else if (grammar.description) {
                    grammarPoints = [grammar.description];
                } else {
                    grammarPoints = ['Practice Korean grammar'];
                }
            }

            const title = grammar.grammarPoint || grammar.title_kr || grammar.titleKR || grammar.title_en || grammar.titleEN || 'Grammar Exercise';

            const convertedExercise = {
                id: grammar.id || `grammar_${index}`,
                title: title,
                english: english || 'Build the sentence',
                korean: korean || 'Complete the exercise',
                wordBank: wordBank,
                grammarPoints: grammarPoints,
                examples: grammar.examples || grammar.step1?.examples || [],
                // Additional Firestore fields mapping
                title_kr: grammar.title_kr || grammar.titleKR,
                title_en: grammar.title_en || grammar.titleEN,
                register: grammar.register,
                grammar_group: grammar.grammar_group,
                forms: grammar.forms
            };

            console.log('✅ Converted exercise:', convertedExercise);
            console.groupEnd();

            return convertedExercise;
        });
    }, [grammarData, dataSource]);

    // Game state
    const [currentExercise, setCurrentExercise] = useState(0);
    const [selectedWords, setSelectedWords] = useState([]);
    const [isCompleted, setIsCompleted] = useState(false);
    const [score, setScore] = useState(0);
    const [totalExercises, setTotalExercises] = useState(grammarExercises.length);
    const [gameMode, setGameMode] = useState('setup'); // 'setup', 'playing', 'result'
    const [exerciseNumber, setExerciseNumber] = useState(1);

    const {
        saveExerciseAttemptWithSession
    } = useSessionProgressActions(user?.uid, parseInt(level), parseInt(chapter), sessionType);

    function resetGame() {
        setCurrentExercise(0);
        setSelectedWords([]);
        setIsCompleted(false);
        setScore(0);
        setGameMode('setup');
    }

    useEffect(() => {
        console.group('📚 GRAMMAR: Component Initialization');
        console.log('📊 Level:', level, 'Chapter:', chapter);
        console.log('📊 Data source:', dataSource);
        console.log('📊 Data auto-selects best available source');
        console.log('📊 Grammar loading:', grammarLoading);
        console.log('📊 Grammar error:', grammarError);
        console.log('📊 Grammar data structure:', {
            hasLearning: !!grammarData?.learning,
            learningLength: grammarData?.learning?.length,
            total: grammarData?.total
        });
        console.log('📊 Exercises array length:', grammarExercises?.length);
        console.log('📊 First exercise:', grammarExercises?.[0]);
        console.groupEnd();

        if (grammarExercises && Array.isArray(grammarExercises)) {
            setTotalExercises(grammarExercises.length);
            console.log('✅ GRAMMAR: Total exercises loaded:', grammarExercises.length);
        } else {
            console.warn('⚠️ GRAMMAR: No exercises loaded or invalid format');
        }

        resetGame();
    }, [grammarData, grammarLoading, grammarError, level, chapter, dataSource, grammarExercises]);

    const startGame = async () => {
        console.log('🎮 Starting Korean Grammar Practice for Level 2 Chapter 1');

        try {
            // Track module start with new tracking system
            if (user?.uid) {
                await TrackingV2Service.updateModuleProgress(
                    user.uid, level, chapter, 'grammarLearn', {
                    userStarted: true,
                    startDate: new Date().toISOString(),
                    lastOpened: new Date().toISOString(),
                    interactionType: 'teaching',
                    totalExercises: grammarExercises.length
                }, {
                    moduleType: 'grammarLearn',
                    interactionType: 'teaching',
                    startMethod: 'user_initiated',
                    exerciseCount: grammarExercises.length
                }
                );

                console.log('✅ Module start tracked successfully');
            }
        } catch (error) {
            console.error('❌ Failed to track module start:', error);

            // Fallback to old tracking system
            if (user?.uid) {
                ChapterProgress.updateModule(user.uid, level, chapter, 'grammarLearn', {
                    user_started: true,
                    start_date: new Date().toISOString(),
                    last_opened: new Date().toISOString(),
                    interaction_type: 'teaching'
                });
            }
        }

        setGameMode('playing');
        setCurrentExercise(0);
        setSelectedWords([]);
        setIsCompleted(false);

        // Mark first grammar point as exposed with new tracking system
        if (user && grammarExercises && grammarExercises[0]) {
            const firstExercise = grammarExercises[0];
            if (firstExercise.id) {
                try {
                    await TrackingV2Service.markGrammarExposed(
                        user.uid, level, chapter, firstExercise.id, 'grammarLearn', {
                        exposureMethod: 'module_start',
                        exposedAt: new Date().toISOString()
                    }
                    );

                    console.log('✅ First grammar point marked as exposed');

                } catch (error) {
                    console.error('❌ Failed to mark grammar as exposed:', error);

                    // Fallback to old tracking system
                    GrammarTracking.markExposed(
                        user.uid,
                        parseInt(level),
                        parseInt(chapter),
                        sessionType,
                        firstExercise.id
                    ).catch(error => {
                        console.error('✗ Failed to mark grammar as exposed:', error);
                    });
                }
            }
        }
    };

    const handleWordClick = (word) => {
        if (gameMode !== 'playing' || isCompleted) return;

        const newSelectedWords = [...selectedWords];
        const existingIndex = newSelectedWords.findIndex(sw => sw.original === word.original);

        if (existingIndex >= 0) {
            // Remove if already selected
            newSelectedWords.splice(existingIndex, 1);
        } else {
            // Add if not selected - automatically place in correct position
            newSelectedWords.push({
                ...word,
                selectedAt: Date.now()
            });

            // Sort by position to maintain correct order
            newSelectedWords.sort((a, b) => a.position - b.position);
        }

        setSelectedWords(newSelectedWords);
    };


    const nextExercise = async () => {
        // Save current exercise attempt with new tracking system
        if (user && grammarExercises && grammarExercises[currentExercise]) {
            const exercise = grammarExercises[currentExercise];
            const isCorrect = selectedWords.length === exercise.wordBank?.length;
            const grammarId = exercise.id || `grammar_${currentExercise}`;
            const exerciseId = `${grammarId}_ex${exerciseNumber}`;

            const trackExerciseAttempt = async () => {
                try {
                    // Mark grammar as exposed
                    await TrackingV2Service.markGrammarExposed(
                        user.uid, level, chapter, grammarId, 'grammarLearn', {
                        exposureMethod: 'exercise_completion',
                        exposedAt: new Date().toISOString()
                    }
                    );

                    // Add question attempt
                    await TrackingV2Service.addQuestionAttempt(
                        user.uid, level, chapter, 'grammarLearn', exerciseNumber, {
                        questionType: 'sentence_building',
                        userResponse: selectedWords.map(w => w.conjugated).join(' '),
                        correctResponse: exercise.korean || '',
                        isCorrect: isCorrect,
                        responseTime: Date.now() - (exercise.startTime || Date.now()),
                        responseOptions: exercise.wordBank?.map(w => w.conjugated) || [],
                        interactionType: 'sentence_building',
                        grammarTitle: exercise.title_kr || exercise.title
                    }, {
                        questionType: 'sentence_building',
                        interactionType: 'sentence_building',
                        grammarTitle: exercise.title_kr || exercise.title,
                    }
                    );

                    // Update grammar mastery
                    await TrackingV2Service.updateGrammarMastery(
                        user.uid, level, chapter, grammarId, 'grammarLearn', {
                        isCorrect: isCorrect,
                        userResponse: selectedWords.map(w => w.conjugated).join(' '),
                        correctResponse: exercise.korean || '',
                        responseTime: Date.now() - (exercise.startTime || Date.now()),
                        interactionType: 'sentence_building',
                        grammarTitle: exercise.title_kr || exercise.title
                    }, {
                        masteryType: 'sentence_building',
                        interactionType: 'sentence_building',
                        grammarTitle: exercise.title_kr || exercise.title
                    }
                    );

                    console.log('✅ Grammar exercise attempt tracked successfully');

                } catch (error) {
                    console.error('❌ Failed to track grammar exercise attempt:', error);

                    // Fallback to old tracking system
                    try {
                        await saveExerciseAttemptWithSession({
                            exercise_id: exerciseId,
                            module_type: moduleType,
                            exercise_number: exerciseNumber,
                            user_answer: selectedWords.map(w => w.conjugated).join(' '),
                            correct_answer: exercise.korean || '',
                            is_correct: isCorrect,
                            time_spent: Date.now() - (exercise.startTime || Date.now()),
                        });
                        console.log('✓ Grammar exercise attempt saved');

                        // Update mastery tracking
                        await GrammarTracking.updateMastery(
                            user.uid,
                            parseInt(level),
                            parseInt(chapter),
                            sessionType,
                            grammarId,
                            {
                                grammar_title: exercise.title_kr || exercise.title,
                                exercise_id: exerciseId,
                                is_correct: isCorrect
                            }
                        );
                        console.log('✓ Grammar mastery updated');
                    } catch (error) {
                        console.error('✗ Failed to save grammar exercise attempt or mastery:', error);
                    }
                }
            };

            // Execute tracking
            trackExerciseAttempt();
        }

        if (currentExercise < totalExercises - 1) {
            setCurrentExercise(prev => prev + 1);
            setSelectedWords([]);
            setExerciseNumber(prev => prev + 1);

            // Mark next grammar point as exposed with new tracking system
            const nextExercise = grammarExercises[currentExercise + 1];
            if (user && nextExercise && nextExercise.id) {
                try {
                    await TrackingV2Service.markGrammarExposed(
                        user.uid, level, chapter, nextExercise.id, 'grammarLearn', {
                        exposureMethod: 'exercise_progression',
                        exposedAt: new Date().toISOString()
                    }
                    );

                    console.log('✅ Next grammar point marked as exposed');

                } catch (error) {
                    console.error('❌ Failed to mark grammar as exposed:', error);

                    // Fallback to old tracking system
                    GrammarTracking.markExposed(
                        user.uid,
                        parseInt(level),
                        parseInt(chapter),
                        sessionType,
                        nextExercise.id
                    ).catch(error => {
                        console.error('✗ Failed to mark grammar as exposed:', error);
                    });
                }
            }
        } else {
            setIsCompleted(true);
            setGameMode('result');

            // Track module completion with new tracking system
            if (user?.uid) {
                const markModuleCompleted = async () => {
                    try {
                        await TrackingV2Service.updateModuleProgress(
                            user.uid, level, chapter, 'grammarLearn', {
                            completion: true,
                            completionTime: new Date().toISOString(),
                            lastWordSeen: grammarExercises[currentExercise]?.id || null,
                            totalExercisesCompleted: totalExercises,
                            exercisesCorrect: score,
                            accuracy: totalExercises > 0 ? Math.round((score / totalExercises) * 100) : 0
                        }, {
                            moduleType: 'grammarLearn',
                            completionMethod: 'user_finished',
                            finalScore: { correct: score, total: totalExercises },
                            accuracy: totalExercises > 0 ? Math.round((score / totalExercises) * 100) : 0
                        }
                        );

                        console.log('✅ Module completion tracked successfully');

                    } catch (error) {
                        console.error('❌ Failed to track module completion:', error);

                        // Fallback to old tracking system
                        ChapterProgress.updateModule(user.uid, level, chapter, 'grammarLearn', {
                            completion: true,
                            completion_time: new Date().toISOString(),
                            last_word_seen: grammarExercises[currentExercise]?.id || null
                        });
                    }
                };

                markModuleCompleted();
            }
        }
    };


    const renderSetupScreen = () => (
        <div className="setup-screen">
            <div className="grammar-setup-content">
                <div className="grammar-instructions">
                    <h3>How to Learn:</h3>
                    <ul>
                        <li><strong>Read</strong> the English sentence</li>
                        <li><strong>Click</strong> on Korean word prototypes</li>
                        <li><strong>Watch</strong> words automatically conjugate and place in correct position</li>
                        <li><strong>Learn</strong> grammar patterns and sentence structure</li>
                    </ul>
                </div>

                <div className="grammar-start-section">
                    <button className="start-button" onClick={startGame}>
                        <i className="fas fa-play"></i>
                        Start Grammar Practice
                    </button>
                </div>
            </div>
        </div>
    );

    const renderGameScreen = () => {
        if (grammarLoading) {
            return (
                <div className="game-screen">
                    <div className="loading-message">
                        <h3>Loading grammar exercises...</h3>
                        <p>Please wait while we prepare your grammar practice.</p>
                    </div>
                </div>
            );
        }

        if (grammarError) {
            return (
                <div className="game-screen">
                    <div className="error-message">
                        <h3>Error loading exercises</h3>
                        <p>{grammarError.message}</p>
                        <button onClick={() => window.location.reload()}>Retry</button>
                    </div>
                </div>
            );
        }

        if (!grammarExercises || grammarExercises.length === 0) {
            return (
                <div className="game-screen">
                    <div className="no-data-message">
                        <h3>No grammar exercises available</h3>
                        <p>There are no grammar exercises for this level and chapter.</p>
                    </div>
                </div>
            );
        }

        const exercise = grammarExercises[currentExercise];

        return (
            <div className="game-screen">
                <div className="game-header">
                    <div className="score-display">
                        Score: {score}/{currentExercise + 1}
                    </div>
                    <div className="progress-display">
                        Exercise {currentExercise + 1} of {totalExercises}
                    </div>
                    <div className="progress-bar">
                        <div
                            className="progress-fill"
                            style={{ width: `${((currentExercise + 1) / totalExercises) * 100}%` }}
                        ></div>
                    </div>
                </div>

                <div className="question-area">
                    <div className="question-header">
                        <h3>Build the Korean sentence:</h3>
                        <div className="english-sentence">
                            <strong>"{exercise.english || 'Build the sentence'}"</strong>
                        </div>
                    </div>

                    <div className="sentence-building-area">
                        <div className="sentence-preview">
                            {selectedWords.length === 0 ? (
                                <div className="empty-sentence">
                                    Click on words below to build your sentence
                                </div>
                            ) : (
                                <div className="built-sentence">
                                    {selectedWords
                                        .sort((a, b) => a.position - b.position)
                                        .map((word, index) => (
                                            <span key={index} className="sentence-word">
                                                {word.conjugated}
                                            </span>
                                        ))
                                    }
                                </div>
                            )}
                        </div>

                        <div className="word-bank">
                            <h4>Click on words in order:</h4>
                            <div className="word-grid">
                                {exercise.wordBank && exercise.wordBank.map((word, index) => {
                                    const isSelected = selectedWords.some(sw => sw.original === word.original);
                                    return (
                                        <button
                                            key={index}
                                            className={`word-button ${isSelected ? 'selected' : ''}`}
                                            onClick={() => handleWordClick(word)}
                                            disabled={isCompleted}
                                        >
                                            <div className="word-original">{word.original}</div>
                                            <div className="word-meaning">{word.meaning}</div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {selectedWords.length > 0 && (
                            <div className="teaching-display">
                                <div className="grammar-explanation">
                                    <h4>Grammar Explanation:</h4>
                                    <p>Watch how the words change when you click them!</p>
                                    <div className="current-sentence">
                                        <strong>Current sentence:</strong> {selectedWords.map(word => word.conjugated).join(' ')}
                                    </div>
                                </div>

                                <div className="grammar-points">
                                    <h4>Grammar Points:</h4>
                                    <ul>
                                        {exercise.grammarPoints && exercise.grammarPoints.map((point, index) => {
                                            // Handle string points (legacy)
                                            if (typeof point === 'string') {
                                                return <li key={index}>{point}</li>;
                                            }

                                            // Handle object points (new RAG structure)
                                            if (typeof point === 'object' && point !== null) {
                                                return (
                                                    <li key={index}>
                                                        {point.rule || point.description || point.explanation || point.text || point.content || point.value || (
                                                            <span>
                                                                {point.usage_rules && (
                                                                    <div><strong>Usage:</strong> {Array.isArray(point.usage_rules) ? point.usage_rules.join(', ') : point.usage_rules}</div>
                                                                )}
                                                                {point.construction_rules && (
                                                                    <div><strong>Construction:</strong> {Array.isArray(point.construction_rules) ? point.construction_rules.join(', ') : point.construction_rules}</div>
                                                                )}
                                                                {!point.usage_rules && !point.construction_rules && (
                                                                    <span>{JSON.stringify(point).slice(0, 100)}...</span>
                                                                )}
                                                            </span>
                                                        )}
                                                    </li>
                                                );
                                            }
                                            return <li key={index}>Invalid grammar point format</li>;
                                        })}
                                        {(!exercise.grammarPoints || exercise.grammarPoints.length === 0) && (
                                            <li>Practice Korean grammar patterns</li>
                                        )}
                                    </ul>
                                </div>

                                {selectedWords.length === exercise.wordBank.length && (
                                    <div className="complete-message">
                                        <div className="correct-result">
                                            <i className="fas fa-check-circle"></i>
                                            <strong>Complete!</strong> You've built the full sentence!
                                        </div>
                                        <div className="final-sentence">
                                            <strong>Final sentence:</strong> {exercise.korean || 'Complete!'}
                                        </div>
                                        <button className="next-exercise-button" onClick={nextExercise}>
                                            {currentExercise < totalExercises - 1 ? 'Next Exercise' : 'Finish'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderResultScreen = () => {
        const accuracy = Math.round((score / totalExercises) * 100);

        return (
            <div className="completion-screen">
                <h2>Grammar Practice Complete!</h2>

                <div className="final-results">
                    <div className="result-card">
                        <h3>Your Score</h3>
                        <div className="score-large">{score}/{totalExercises}</div>
                        <div className="accuracy-large">{accuracy}% Accuracy</div>
                    </div>

                    <div className="result-card">
                        <h3>Grammar Points Mastered</h3>
                        <ul>
                            <li>Verb Conjugation</li>
                            <li>Particle Usage</li>
                            <li>Sentence Structure</li>
                            <li>Medical Vocabulary</li>
                        </ul>
                    </div>
                </div>

                <div className="completion-actions">
                    <button className="play-again-button" onClick={resetGame}>
                        <i className="fas fa-redo"></i>
                        Practice Again
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="grammar-feature-container">
            {/* Data Source Indicator (auto-selects best available) */}
            <div className="module-data-source-toggle">
                <div className="data-source-indicator">
                    <span className={`source-badge ${dataSource.toLowerCase().replace(' ', '-')}`}>
                        {dataSource}
                    </span>
                </div>
            </div>

            {gameMode === 'setup' && renderSetupScreen()}
            {gameMode === 'playing' && renderGameScreen()}
            {gameMode === 'result' && renderResultScreen()}
        </div>
    );
};

export default GrammarFeature;
